/**
 * Configuração da plataforma (D615 SETUP-READINESS) — the pure side of the screen that lists what is still missing for the
 * platform to work end to end: where the operator fixes each item, which vendor console it points at, how the server's
 * readings are grouped and how the progress reads. The words come from the central vocabulary (`setup.*`), never from here.
 *
 * Nothing here decides whether something is configured: the server reads the real runtime state (`GET /v1/admin/setup-readiness`)
 * and this module only arranges its answer.
 */
import {
  SETUP_AREAS,
  SETUP_ITEM_IDS,
  type SetupArea,
  type SetupItemId,
  type SetupItemReadingView,
  type SetupReadinessView,
} from '@vello/api-client';

/** The name of an Admin route (a key of `ADMIN_ROUTES`). */
export type AdminRouteName = string;

/** Fixed fix routes: the screen where the operator corrects an item; `undefined` = no screen (a server-side step, told in the steps). */
export const SETUP_FIX_ROUTES: Readonly<Record<SetupItemId, AdminRouteName | undefined>> = {
  credential_vault: undefined,
  operator_mfa: 'operators',
  events_alive: undefined,
  worker_alive: undefined,
  email: 'integrations',
  email_domain: 'integrations',
  otp_whatsapp: 'integrations_otp_setup',
  otp_sms: 'integrations_otp_setup',
  otp_webhook: 'integrations_otp_setup',
  otp_country: 'governed_settings',
  otp_test_code: undefined,
  push: undefined,
  geo_providers: 'integrations',
  geo_cascade: 'governed_settings',
  map_tiles: 'integrations',
  wallet_top_up: 'integrations',
  online_payments: undefined,
  analytics_reader: undefined,
  analytics_pseudonym: undefined,
  markets: 'markets',
  market_status: 'markets',
  country_pack: 'registries',
  kyc_catalog: 'kyc_catalog',
  market_tariffs: 'tariff_categories',
  market_service_area: 'service_areas',
  market_policies: 'governed_settings',
  country_policies: 'governed_settings',
  market_catalog: 'service_categories',
  central_teams: 'central_teams',
  widget_market: 'integrations_widgets',
  market_ride_policies: 'governed_settings',
  market_food_policies: 'governed_settings',
  market_alerts: 'governed_settings',
};

/** The screen that fixes this reading: a market without categories is fixed in the service catalog, not in the tariffs. */
export function fixRouteFor(item: Pick<SetupItemReadingView, 'id' | 'reasonCode'>): AdminRouteName | undefined {
  if (item.id === 'market_tariffs' && item.reasonCode === 'SETUP_NO_CATEGORIES') return 'service_categories';
  return SETUP_FIX_ROUTES[item.id];
}

/** One external console: the vendor's name (a brand, not translated) and its https address. */
export interface ProviderLink {
  readonly vendor: string;
  readonly url: string;
}

const RESEND_KEYS: ProviderLink = { vendor: 'Resend', url: 'https://resend.com/api-keys' };
const RESEND_DOMAINS: ProviderLink = { vendor: 'Resend', url: 'https://resend.com/domains' };
const META_WHATSAPP: ProviderLink = { vendor: 'Meta', url: 'https://business.facebook.com/wa/manage/home/' };

/** The vendor consoles an item points at (https only). An item whose vendor is its own choice (SMS) has none. */
const PROVIDER_LINKS: Readonly<Partial<Record<SetupItemId, readonly ProviderLink[]>>> = {
  email: [RESEND_KEYS],
  email_domain: [RESEND_DOMAINS],
  otp_whatsapp: [META_WHATSAPP],
  otp_webhook: [META_WHATSAPP],
  geo_providers: [
    { vendor: 'HERE', url: 'https://platform.here.com/' },
    { vendor: 'Google Maps', url: 'https://console.cloud.google.com/google/maps-apis/credentials' },
  ],
  push: [{ vendor: 'Expo', url: 'https://expo.dev/settings/access-tokens' }],
};

/**
 * The vendor console(s) of an item. A country's own provider (the wallet top-up PSP) is never named here: the COUNTRY PACK ships its console and the
 * server reports it (`item.console`); the console takes it only when it is https.
 */
export function providerConsoleLinks(item: Pick<SetupItemReadingView, 'id' | 'console'>): readonly ProviderLink[] {
  if (item.console !== undefined) return /^https:\/\/[^\s@]+$/.test(item.console.url) ? [{ vendor: item.console.name, url: item.console.url }] : [];
  return PROVIDER_LINKS[item.id] ?? [];
}

/** Every address the platform table can produce — for the https-only test. */
export function allProviderUrls(): readonly string[] {
  return Object.values(PROVIDER_LINKS).flatMap((l) => l ?? []).map((l) => l.url);
}

/**
 * Whether the reading still asks something of the operator. `ok` is done; `not_built` is something the product does not have yet;
 * `unknown` could not be verified (neither done nor proven missing, the server counts it in neither).
 */
export function isPending(item: Pick<SetupItemReadingView, 'state'>): boolean {
  return item.state !== 'ok' && item.state !== 'not_built' && item.state !== 'unknown';
}

/** A stable React/DOM key: the same item id repeats per country and per market. */
export function setupItemKey(item: { readonly id: string; readonly country?: string; readonly market?: string }): string {
  return `${item.id}|${item.country ?? ''}|${item.market ?? ''}`;
}

export interface SetupSection {
  readonly area: SetupArea;
  /** Still asks something of the operator, in catalog order. */
  readonly pending: readonly SetupItemReadingView[];
  /** Ready. */
  readonly done: readonly SetupItemReadingView[];
  /** Not built yet, or could not be verified. */
  readonly informative: readonly SetupItemReadingView[];
}

const ITEM_ORDER: ReadonlyMap<string, number> = new Map(SETUP_ITEM_IDS.map((id, i) => [id, i]));
const LEVEL_ORDER: Readonly<Record<string, number>> = { platform: 0, country: 1, market: 2 };

function byCatalog(a: SetupItemReadingView, b: SetupItemReadingView): number {
  return (
    (ITEM_ORDER.get(a.id) ?? 0) - (ITEM_ORDER.get(b.id) ?? 0) ||
    (LEVEL_ORDER[a.level] ?? 0) - (LEVEL_ORDER[b.level] ?? 0) ||
    (a.country ?? '').localeCompare(b.country ?? '') ||
    (a.market ?? '').localeCompare(b.market ?? '')
  );
}

/** The readings by area (`SETUP_AREAS` order): pending first, then the done ones, then the informative; an area with nothing is left out. */
export function groupSetupItems(view: Pick<SetupReadinessView, 'items'>): readonly SetupSection[] {
  const sections: SetupSection[] = [];
  for (const area of SETUP_AREAS) {
    const items = view.items.filter((i) => i.area === area).slice().sort(byCatalog);
    if (items.length === 0) continue;
    sections.push({
      area,
      pending: items.filter(isPending),
      done: items.filter((i) => i.state === 'ok'),
      informative: items.filter((i) => i.state === 'not_built' || i.state === 'unknown'),
    });
  }
  return sections;
}

export type SetupProgressTone = 'success' | 'warning' | 'neutral';

export interface SetupProgress {
  readonly ready: number;
  readonly required: number;
  readonly pending: number;
  /** Required items that could not be checked now (neither done nor proven missing). */
  readonly unverified: number;
  /** Nothing is required of the operator in this scope (no item was readable). */
  readonly empty: boolean;
  /** Every required item is ready and none is unverified — the only state that reads "all set". */
  readonly complete: boolean;
  /** Nothing pending was found, but some required items could not be verified. */
  readonly unverifiedOnly: boolean;
  readonly tone: SetupProgressTone;
}

/** The progress of the summary: the server's counts, never recomputed from the items. An unverified item never reads as done. */
export function progressOf(summary: SetupReadinessView['summary']): SetupProgress {
  const unverified = summary.unverified ?? 0;
  const empty = summary.required <= 0;
  const complete = !empty && summary.pending <= 0 && unverified <= 0 && summary.ready >= summary.required;
  const unverifiedOnly = !empty && summary.pending <= 0 && unverified > 0;
  return {
    ready: summary.ready, required: summary.required, pending: summary.pending, unverified, empty, complete, unverifiedOnly,
    tone: empty || unverifiedOnly ? 'neutral' : complete ? 'success' : 'warning',
  };
}
